import { useCallback, useMemo } from "react";

import { queryClient } from "@src/queryClient";

import {
  emitTourGuideEvent,
  type TourGuideEventPayload,
  type TourId,
  type TourStepActionName,
  type TourStepId,
  type TourStepIdFor,
  useTourGuideStore,
} from "../domain";

type TourStepActionRunner = (
  payload?: Omit<TourGuideEventPayload, "tourId">,
) => Promise<boolean>;

/**
 * Runners for the user-triggered actions declared on a tour step, keyed by the
 * action names inferred from the tour's registered steps.
 */
export type TourStepActionRunners<
  TTourId extends TourId,
  TStepId extends TourStepId,
> = Record<TourStepActionName<TTourId, TStepId>, TourStepActionRunner>;

const EMPTY_STEP_IDS: TourStepId[] = [];

export function useTourGuide(tourId: TourId) {
  const activeStepId = useTourGuideStore(
    (state) => state.tours[tourId]?.activeStepId ?? null,
  );
  const stepIds = useTourGuideStore(
    (state) => state.tours[tourId]?.stepIds ?? EMPTY_STEP_IDS,
  );
  const startTour = useTourGuideStore((state) => state.startTour);
  const stopTour = useTourGuideStore((state) => state.stopTour);
  const nextStep = useTourGuideStore((state) => state.nextStep);
  const previousStep = useTourGuideStore((state) => state.previousStep);
  const goToStep = useTourGuideStore((state) => state.goToStep);

  return {
    activeStepId,
    isActive: activeStepId !== null,
    stepIds,
    startTour: useCallback(() => startTour(tourId), [startTour, tourId]),
    stopTour: useCallback(() => stopTour(tourId), [stopTour, tourId]),
    nextStep: useCallback(() => nextStep(tourId), [nextStep, tourId]),
    previousStep: useCallback(
      () => previousStep(tourId),
      [previousStep, tourId],
    ),
    goToStep: useCallback(
      (stepId: TourStepId) => goToStep(tourId, stepId),
      [goToStep, tourId],
    ),
  };
}

export function useActiveTourGuide() {
  const activeTourId = useTourGuideStore((state) => state.activeTourId);
  const activeStepId = useTourGuideStore((state) =>
    state.activeTourId
      ? (state.tours[state.activeTourId]?.activeStepId ?? null)
      : null,
  );

  return {
    activeTourId,
    activeStepId,
    isActive: activeTourId !== null,
  };
}

export function useTourGuideStep<
  const TTourId extends TourId,
  const TStepId extends TourStepIdFor<TTourId> & TourStepId,
>(tourId: TTourId, stepId: TStepId) {
  const activeStepId = useTourGuideStore(
    (state) => state.tours[tourId]?.activeStepId ?? null,
  );
  const step = useTourGuideStore(
    (state) => state.tours[tourId]?.steps[stepId] ?? null,
  );
  const isActive = activeStepId === stepId;

  return {
    step,
    isActive,
    actions: useMemo(() => {
      const entries = Object.entries(step?.actions ?? {}).map(
        ([name, actionConfig]) => [
          name,
          async (payload: Omit<TourGuideEventPayload, "tourId"> = {}) => {
            const event = {
              type: actionConfig.event,
              payload: {
                ...payload,
                tourId,
              },
            };

            if (actionConfig.matches && !actionConfig.matches(event)) {
              return false;
            }

            const result = await actionConfig.action?.({
              event,
              queryClient,
            });

            if (result === false) {
              return false;
            }

            emitTourGuideEvent(event);
            return true;
          },
        ],
      );

      return Object.fromEntries(entries) as TourStepActionRunners<
        TTourId,
        TStepId
      >;
    }, [step?.actions, tourId]),
  };
}

export function useIsTourActive(tourId: TourId) {
  const activeTourId = useTourGuideStore((state) => state.activeTourId);
  return activeTourId === tourId;
}
